<template>
    <div
        :class="['manage-time-entries', isMobile ? 'mobile' : 'desktop', taskId === 0 ? 'new-task' : 'view-task']"
    >
        <BillableHeader
            :time-entries="time_entries"
            :tracked-time="trackedTime"
            :controller="controller"
            :is-mobile="isMobile"
            @close="$emit('close')"
        />
        <BillableTable
            v-if="!loading"
            ref="billableTable"
            :admin-view="adminView"
            :time_entries="time_entries"
            :is-mobile="isMobile"
            :is-read-only="isReadOnly"
            :controller="controller"
            :company="company"
            :current-limit="currentLimit"
            @edit-time-entry="selectedCommentTimeEntryId = $event"
            @load-more="onLoadMore"
        />
        <EditTimeEntryCommentDialog
            v-if="controller.model.isEditTimeEntryDescriptionVisible()"
            :controller="controller"
        />
    </div>
</template>

<script>
import Vue from "vue";
import {ref, computed, reactive, watch} from "@vue/composition-api";
import { $t } from "@/i18n";
import AppModule from "../../../store/modules/app";
import AccountModule from "../../../store/modules/account";
import TaskModule from "../../../store/modules/task";
import TimerModule from "../../../store/modules/timer";
import BillableButton from "@/views/Task/TimeEntries/BillableButton";
import {
    parseSysDate,
} from "../../../plugins/formatting";
import BillableHeader from "@/views/Task/TimeEntries/BillableHeader";
import BillableTable from "@/views/Task/TimeEntries/BillableTable";
import EditTimeEntryCommentDialog from "@/views/Task/TimeEntries/EditTimeEntryCommentDialog";
import Controller from "./controller"

export default {
    name: "TaskTimeEntriesControl",
    filters: {
        day_diff(time_entry) {
            let start = parseSysDate(time_entry.date);
            let end = parseSysDate(time_entry.end_date);
            let diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

            if (diffDays === 0) return "";
            if (diffDays === 1) return $t("+1 day");
            return "{0}{1} days".format(diffDays >= 0 ? "+" : "-", Math.abs(diffDays));
        }
    },
    components: {
        EditTimeEntryCommentDialog,
        BillableTable,
        BillableHeader,
        BillableButton
    },
    props: {
        accountId: {
            validator: prop => typeof prop === "number" || prop === null,
            default: null
        },
        taskId: {
            type: [Number, String],
            required: true
        },
        taskVirtualId: {
            type: [Number, String],
            default: null
        },
        taskRights: {
            type: Number,
            required: true
        },
        trackedTime: {
            type: Number,
            required: true
        },
        isTaskRunning: {
            type: Boolean,
            required: true
        },
        runningTime: {
            type: Number,
            required: true
        },
        isMobile: {
            type: Boolean,
            required: true
        },
        existingTimeEntry: {
            type: Object,
            default: null
        }
    },
    setup(props, ctx) {
        const { rights_masks } = AppModule();
        const { task_dialog } = TaskModule();
        const timerModule = TimerModule();
        const billableTable = ref(null);
        const { current_account, available_company_accounts } = AccountModule();
        const selectedCommentTimeEntryId = ref(null);
        const currentLimit = ref(100);

        const locale = computed(function () {
            return Vue.formatting.culture;
        });
        const adminView = computed(function () {
            return (
                task_dialog.value.admin_view &&
                (props.taskRights & rights_masks.value.manage_task) === rights_masks.value.manage_task
            );
        });

        const company = computed(() => {
            return available_company_accounts.value.find(x => x.id === props.accountId)
        })

        const { accounts_info } = AccountModule();
        const isReadOnly = computed(function () {
            // let debug = false;
            // for (let i in accounts_info.value) console.log(accounts_info.value[i]);

            // New task case
            if (props.taskId === 0) return false;
            if (props.accountId == null || props.accountId === 0) return false;

            // Superusers can edit times
            // let is_client_manager = (props.taskRights & rights_masks.value.manage_account) === rights_masks.value.manage_account;
            let task_account = accounts_info.value.filter(x => x.id === props.accountId)[0];
            // if (debug) console.log(task_account);
            let is_admin = (task_account.rights & rights_masks.value.manage_account) === rights_masks.value.manage_account;
            // if (debug) console.log('is_admin = ' + is_admin + ', rights = ' + task_account.rights);
            if (is_admin) return false;

            // Users cannot edit times
            if (task_account.data === undefined || task_account.data.topic_management_disabled_for_users === undefined) return false;
            let time_management_disabled_for_users = (task_account.data.time_management_disabled_for_users === 1);
            // if (debug) console.log('time_management_disabled_for_users = ' + (time_management_disabled_for_users ? 'true' : 'false'));

            if (time_management_disabled_for_users) return true;
            return false;
        });

        const onEmit = (action, params) => {
            ctx.emit(action, params)
        }

        /** @type {Controller} */
        const controller = reactive(new Controller(
            props.isMobile,
            adminView.value,
            props.taskId,
            timerModule.task_id.value && timerModule.status.value === 'running' ? Number(timerModule.task_id.value) : null,
            current_account.value.id,
            isReadOnly,
            props.taskVirtualId,
            onEmit
        ))

        const loading = ref(false);
        const time_entries = ref([]);
        const last_time_entry = ref(null);

        const focus_duration = function () {
            if (billableTable.value == null) {
                setTimeout(focus_duration, 200);
            } else {
                if (billableTable.value && billableTable.value.$refs.duration && billableTable.value.$refs.duration.length) {
                    billableTable.value.$refs.duration[0].focus();
                } else {
                    setTimeout(focus_duration, 200);
                }
            }
        };

        watch(() => timerModule.status.value, (val) => {
            controller.setRunningTaskId(timerModule.task_id.value && timerModule.status.value === 'running' ? Number(timerModule.task_id.value) : null)
        })

        controller.loadData(timerModule.status.value, props.existingTimeEntry, company.value, currentLimit.value)
        focus_duration()

        const onLoadMore = () => {
            currentLimit.value = currentLimit.value + 50
            controller.loadData(timerModule.status.value, props.existingTimeEntry, company.value, currentLimit.value)
        }

        return {
            time_entries,
            loading,
            locale,
            adminView,
            isReadOnly,
            selectedCommentTimeEntryId,
            last_time_entry,
            /** @type {Controller} */
            controller,
            billableTable,
            onLoadMore,
            currentLimit,
            company
        };
    }
};
</script>
